/**
 * UTM Campaign Tracker for Shopify
 * -----------------------------------
 * Detects UTM parameters from the current URL (or sessionStorage fallback)
 * and stores them as Shopify cart attributes via the Cart API.
 *
 * UTM parameters captured:
 *   utm_source, utm_medium, utm_campaign, utm_term, utm_content, utm_id
 *
 * Cart attributes written (example):
 *   _utm_source    → "google"
 *   _utm_medium    → "cpc"
 *   _utm_campaign  → "spring_sale_2026"
 *   _utm_term      → "running+shoes"
 *   _utm_content   → "banner_v2"
 *
 * Usage:
 *   Paste this script into your Shopify theme's theme.liquid just before </body>,
 *   or load it as an asset: {{ 'utm-cart-tracker.js' | asset_url | script_tag }}
 *
 * Compatibility: All modern browsers + IE11 (no dependencies required).
 */

(function () {
  "use strict";

  // ─── Configuration ────────────────────────────────────────────────────────

  /** All UTM keys to look for in the URL */
  var UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
  ];

  /**
   * Prefix applied to cart attribute keys.
   * Single underscore hides them from customers on the storefront
   * while keeping them visible in the Shopify admin order view.
   */
  var CART_ATTR_PREFIX = "_";

  /**
   * sessionStorage key used to persist UTM data across page navigations
   * within the same browser session (e.g. user clicks to PDP from landing page).
   */
  var SESSION_STORAGE_KEY = "shopify_utm_params";

  /**
   * How long to wait (ms) after page load before writing UTM attributes.
   * This ensures the UTM write happens after Rebuy, currency apps, and
   * theme cart initializations — preventing them from overwriting our attributes.
   * Increase if you have slow-loading apps that update the cart.
   */
  var WRITE_DELAY = 2000;

  /**
   * Retry settings for first-load cart initialization.
   * On brand new sessions Shopify may not have a cart yet — we retry
   * a few times with a short delay before giving up.
   */
  var MAX_RETRIES = 5;
  var RETRY_DELAY = 800; // ms between attempts

  /**
   * Set to true to log debug info to the browser console.
   * Flip to false (or remove) before going to production.
   */
  var DEBUG = false;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ["[UTM Tracker]"].concat(Array.prototype.slice.call(arguments)));
    }
  }

  /** Parse UTM params from a URL search string */
  function parseUTMFromSearch(search) {
    var params = {};
    var query = (search || "").replace(/^\?/, "");
    if (!query) return params;

    query.split("&").forEach(function (pair) {
      var parts = pair.split("=");
      var key = decodeURIComponent(parts[0] || "");
      var value = decodeURIComponent((parts[1] || "").replace(/\+/g, " "));
      if (UTM_KEYS.indexOf(key) !== -1 && value) {
        params[key] = value;
      }
    });

    return params;
  }

  /** Read previously saved UTM data from sessionStorage */
  function loadFromSession() {
    try {
      var raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  /** Persist UTM data to sessionStorage for cross-page continuity */
  function saveToSession(params) {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(params));
    } catch (e) {
      // sessionStorage unavailable (private mode / storage full) — safe to ignore
    }
  }

  /**
   * Ensure a Shopify cart session exists.
   * On fresh sessions /cart/update.js can 422 before a cart token is set.
   * We POST to /cart/add.js with an empty items array first — this is a
   * no-op that forces Shopify to create the cart cookie if missing.
   * Calls callback(err) when done.
   */
  function ensureCartSession(callback) {
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Cart exists and has a token — we're good
        if (cart && cart.token) {
          log("Cart session exists, token:", cart.token);
          callback(null);
        } else {
          // No token yet — force creation
          log("No cart token found, initialising cart session...");
          forceCartCreation(callback);
        }
      })
      .catch(function () {
        // /cart.js failed entirely — try forcing creation anyway
        forceCartCreation(callback);
      });
  }

  /**
   * Force Shopify to create a cart session by hitting /cart/add.js
   * with an empty payload. This sets the cart cookie without adding items.
   */
  function forceCartCreation(callback) {
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [] }),
    })
      .then(function () { callback(null); })
      .catch(function (err) { callback(err); });
  }

  /**
   * Update Shopify cart attributes via the Ajax Cart API, with retry logic.
   * Retries up to MAX_RETRIES times if the response is not 2xx,
   * waiting RETRY_DELAY ms between attempts.
   *
   * @param {Object}   attributes
   * @param {number}   attempt     current attempt number (starts at 1)
   * @param {Function} callback
   */
  function updateCartAttributes(attributes, attempt, callback) {
    var payload = JSON.stringify({ attributes: attributes });

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error("HTTP " + res.status);
        }
        return res.json();
      })
      .then(function (cart) {
        log("Cart attributes updated on attempt " + attempt + ":", cart.attributes);
        if (typeof callback === "function") callback(null, cart);
      })
      .catch(function (err) {
        log("Attempt " + attempt + " failed:", err.message);
        if (attempt < MAX_RETRIES) {
          log("Retrying in " + RETRY_DELAY + "ms...");
          setTimeout(function () {
            updateCartAttributes(attributes, attempt + 1, callback);
          }, RETRY_DELAY);
        } else {
          log("All " + MAX_RETRIES + " attempts failed — UTM params saved to sessionStorage for next page.");
          if (typeof callback === "function") callback(err);
        }
      });
  }

  // ─── Main Logic ───────────────────────────────────────────────────────────

  function init() {
    // 1. Parse UTM params from the current URL
    var fromURL = parseUTMFromSearch(window.location.search);
    log("UTM params from URL:", fromURL);

    // 2. Load any previously stored params from sessionStorage
    var fromSession = loadFromSession();
    log("UTM params from session:", fromSession);

    // 3. Merge: URL params take priority over session (last-touch wins)
    //    Change to `Object.assign({}, fromURL, fromSession)` if you prefer first-touch.
    var merged = {};
    UTM_KEYS.forEach(function (key) {
      var val = fromURL[key] || fromSession[key];
      if (val) merged[CART_ATTR_PREFIX + key] = val;
    });

    log("Merged UTM params:", merged);

    // 4. If no UTM data found at all, nothing to do
    if (Object.keys(merged).length === 0) {
      log("No UTM params found — skipping cart update.");
      return;
    }

    // 5. Always persist to sessionStorage first so params survive even if
    //    the cart API calls fail on this page load
    saveToSession(merged);

    // 6. Wait before writing to the cart so other apps (Rebuy, currency
    //    switchers, theme cart init) have finished their own cart updates first.
    //    Without this delay those updates can overwrite our UTM attributes.
    log("Waiting " + WRITE_DELAY + "ms before writing to cart...");
    setTimeout(function () {

      // 7. Ensure a cart session exists before writing attributes
      ensureCartSession(function (err) {
        if (err) {
          log("Could not initialise cart session:", err);
        }
        // Attempt to write regardless — retry logic will handle transient failures
        updateCartAttributes(merged, 1, function (err) {
          if (!err) {
            log("Successfully wrote UTM params to cart.");
          }
        });
      });

    }, WRITE_DELAY);
  }

  // Run after DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();